import { RpcError } from "grpc-web";
import React, { createContext, useContext, useState } from "react";

// Hooks
import useClaimsToast from "../hooks/useToast";

// Protos
import { ClaimsLampCase, GetClaimsLampCaseResponse } from "../utils/api/protos/messages/cases.message_pb";
import { CaseClaimDetail, GetClaimDetailListingResponse } from "../utils/api/protos/messages/claims.message_pb";
import { HasPolicyByMemberIdResponse } from "../utils/api/protos/messages/members.message_pb";

// Utils
import { getClaimsLampCase } from "../utils/api/cases";
import { getClaimDetailListing } from "../utils/api/claims";
import { hasPolicyByMemberId } from "../utils/api/members";

interface AttorneyContextType {
	isLoading: boolean;
	claimCase: ClaimsLampCase | undefined;
	claimList: Array<CaseClaimDetail>;
	handleFetchLampCase: (caseId: number) => Promise<void>;
	handleFetchClaimDetails: () => Promise<void>;
	handleFetchHasPolicy: () => Promise<boolean>;
}

interface AttorneyProviderProps {
	children: React.ReactNode;
}

const AttorneyConext = createContext<AttorneyContextType | undefined>(undefined);

export const AttorneyProvider: React.FC<AttorneyProviderProps> = ({ children }) => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [claimList, setClaimList] = useState<Array<CaseClaimDetail>>([]);
	const [claimCase, setClaimCase] = useState<ClaimsLampCase | undefined>();
	const toast = useClaimsToast();

	const handleFetchLampCase = async (caseId: number): Promise<void> => {
		setIsLoading(true);

		getClaimsLampCase(caseId)
			.then((response: GetClaimsLampCaseResponse) => {
				setClaimCase(response.getCase());
			})
			.catch((err) => {
				toast("Error", (err as RpcError).message, "error");
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const handleFetchClaimDetails = async (): Promise<void> => {
		setIsLoading(true);

		if (claimCase) {
			getClaimDetailListing(claimCase.getCaseid())
				.then((response: GetClaimDetailListingResponse) => {
					setClaimList(response.getClaimsList());
				})
				.catch((err) => {
					toast("Error", (err as RpcError).message, "error");
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	};

	const handleFetchHasPolicy = async (): Promise<boolean> => {
		setIsLoading(true);

		if (claimCase && claimCase.getMemberid() !== 0 && claimCase.getMemberid() !== null) {
			return hasPolicyByMemberId(claimCase.getMemberid())
				.then((response: HasPolicyByMemberIdResponse) => {
					setIsLoading(false);
					return response.getHasPolicy();
				})
				.catch(() => {
					// Don't show a UI error, just let it be logged to the console
					setIsLoading(false);
					return false;
				});
		} else {
			return Promise.resolve(false);
		}
	};

	return (
		<AttorneyConext.Provider
			value={{
				isLoading,
				claimList,
				claimCase,
				handleFetchLampCase,
				handleFetchClaimDetails,
				handleFetchHasPolicy
			}}>
			{children}
		</AttorneyConext.Provider>
	);
};

export const useAttorneyProvider = () => {
	const context = useContext(AttorneyConext);
	if (!context) throw new Error("useAttorneyProvider must be used within a ClaimProvider");
	return context;
};
